import { Box, Button, IconButton } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { DataGrid, GridAddIcon, type GridColDef } from '@mui/x-data-grid';

import PageBaseLayout from '../layouts/PageBaseLayout';
import { Outlet } from 'react-router';
import { useEffect, useState } from 'react';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { supabase } from '../supabase-client';
import type { WithdrawalRequest } from '../models/types';
import useDialog from '../hooks/useDialog';
import moment from 'moment';
import ProcessWithdrawalRequestDialog from '../components/ProcessWithdrawalRequestDialog';

export default function WithdrawalRequests() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const [withdrawalRequests, setWithdrawalRequests] = useState<
    WithdrawalRequest[]
  >([]);

  const handleUpdateWithdrawalRequest = (
    id: number,
    newStatus: 'pending' | 'completed' | 'rejected',
    newAmount: number,
    newRemarks: string | undefined,
  ) => {
    setWithdrawalRequests((oldWithdrawalRequests) => {
      const withdrawalRequest = oldWithdrawalRequests.find(
        (wr) => wr.id === id,
      );

      if (!withdrawalRequest)
        throw new Error('Could not find withdrawal request. Please refresh.');

      withdrawalRequest.status = newStatus;
      withdrawalRequest.amount = Math.abs(newAmount) * -1;
      withdrawalRequest.remarks = newRemarks ?? '';

      const newWithdrawalRequests = oldWithdrawalRequests.filter(
        (wr) => wr.id !== withdrawalRequest.id,
      );

      newWithdrawalRequests.push(withdrawalRequest);
      newWithdrawalRequests.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      return newWithdrawalRequests;
    });
  };

  const columns: GridColDef<WithdrawalRequest>[] = [
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      renderCell: (param) => {
        const requestNotPending = param.row.status !== 'pending';

        return (
          <Box>
            <IconButton
              disabled={requestNotPending}
              aria-label="approve"
              onClick={() => {
                dialog.handleDialogOpen(
                  <ProcessWithdrawalRequestDialog
                    handleUpdateWithdrawalRequest={
                      handleUpdateWithdrawalRequest
                    }
                    withdrawalRequestId={param.row.id}
                    amount={param.row.amount}
                    newStatus="completed"
                  />,
                  `Approve Withdrawal Request - ${param.row.id}`,
                );
              }}>
              <DoneIcon color={requestNotPending ? 'disabled' : 'success'} />
            </IconButton>
            <IconButton
              disabled={requestNotPending}
              aria-label="reject"
              onClick={() => {
                dialog.handleDialogOpen(
                  <ProcessWithdrawalRequestDialog
                    handleUpdateWithdrawalRequest={
                      handleUpdateWithdrawalRequest
                    }
                    withdrawalRequestId={param.row.id}
                    amount={param.row.amount}
                    newStatus="rejected"
                  />,
                  `Reject Withdrawal Request - ${param.row.id}`,
                );
              }}>
              <ClearIcon color={requestNotPending ? 'disabled' : 'error'} />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'username',
      headerName: 'Username',
      width: 160,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
    },
    {
      field: 'phone_no',
      headerName: 'Phone No',
      width: 100,
      type: 'string',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      type: 'dateTime',
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
      type: 'number',
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 140,
      type: 'string',
    },
  ];

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase.functions.invoke(
        'withdrawal-requests',
        { method: 'GET' },
      );

      if (error) {
        console.error('Error fetching withdrawal requests:', error);
        throw error;
      }

      setWithdrawalRequests(data.data);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout
      title="Withdrawal Requests"
      //   actions={
      //     <Button
      //       component={Link}
      //       to="./new"
      //       startIcon={<GridAddIcon />}
      //       size="small"
      //       variant="contained">
      //       New Campaign
      //     </Button>
      //   }
    >
      {/* <Button
        onClick={async () => {
          const response = await supabase.functions.invoke(
            'vouchers/WEILONGVOUCHER2/redeem',
            { method: 'POST' },
          );
          console.log(response.data);
        }}
        startIcon={<GridAddIcon />}
        size="small"
        variant="contained">
        xdts
      </Button> */}
      <DataGrid columns={columns} rows={withdrawalRequests} />
      <Outlet />
    </PageBaseLayout>
  );
}
